import type { ActivityProductivity, AgentEventType } from '@doneverse/shared'
import { isSearchableTerm } from '@doneverse/shared'
import { DEFAULT_EVENT_TYPES } from './activityLogCells'
import { DURATION_SORTS, type DurationSort } from './activityLogSort'
import { isSearchDate, type PresetKey } from './activityRange'
import { PRODUCTIVITY_STATUSES } from '@/components/activity/productivity'

/** What the toolbar's idle control asks for, beyond the default view.
 *
 *  Absent is the third state, "hide idle", rather than a value of its own. One
 *  param with three reachable states cannot express a contradiction; a pair of
 *  booleans (`idle=false&idleOnly=true`) can, and someone eventually pastes it.
 *  If `86d4a9qqh` lands a Passive state, this union grows a member and nothing
 *  else here changes shape. */
export type IdleFilter = 'include' | 'only'

/** `shift` is the Activity Log's own preset (ClickUp 86d4a9rcb). `PresetKey` is
 *  shared with `TimeRangeActivityPanel`, which has no shift view, so it stays put. */
export type ActivityLogPreset = PresetKey | 'shift'

export interface AdminActivityLogSearch {
  doerId?: string
  preset?: ActivityLogPreset
  idle?: IdleFilter
  /** Absent is All. Applied in the browser over the whole range (ClickUp
   *  14zf25zjet4); dropped under idle-only, whose rows never have a status. */
  productivity?: ActivityProductivity
  /** Absent is time order, the server's own. */
  sort?: DurationSort
  q?: string
  from?: string
  to?: string
  /** Shift preset only: which shift day (absent = the one containing now). ClickUp 86d4a9rcb. */
  at?: string
  /** Shift preset only, as a pair: a selection on the timeline (a drag snapped to
   *  whole 5-minute blocks, or one clicked block). The table shows this window. */
  start?: string
  end?: string
}

const PRESETS: readonly ActivityLogPreset[] = ['shift', 'today', 'last24h', 'last7d', 'custom']
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/
const isInstant = (v: unknown): v is string =>
  typeof v === 'string' && ISO_INSTANT.test(v) && Number.isFinite(Date.parse(v))

/** A search term as the router hands it over. TanStack parses search values
 *  as JSON, so `?q=1234` arrives as the NUMBER 1234 (the same reason legacy
 *  `?idle=true` arrives as a boolean above). Accept both, trim, and keep only a term the API accepts. */
export function searchTermFromParam(value: unknown): string | undefined {
  const raw = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : undefined
  if (raw === undefined) return undefined
  const q = raw.trim()
  return isSearchableTerm(q) ? q : undefined
}

/** The term a search box writes to the URL: trimmed, or undefined when the
 *  API would reject it (`isSearchableTerm`: too short, or too few characters
 *  besides `*`). Page effects compare the box against the URL through this,
 *  so a box holding "ma" (which writes nothing) is not mistaken for a URL that
 *  disagrees with it. */
export function searchBoxTerm(value: string): string | undefined {
  const t = value.trim()
  return isSearchableTerm(t) ? t : undefined
}

/** The Activity Log's URL contract, in one place so the page and the router
 *  cannot drift on what a link means.
 *
 *  Unrecognised values are dropped rather than passed through, which is what
 *  makes the page's own types honest: anything reaching `AdminActivityLog` has
 *  already been narrowed here. */
export function validateActivityLogSearch(search: Record<string, unknown>): AdminActivityLogSearch {
  const result: AdminActivityLogSearch = {}

  if (typeof search.doerId === 'string' && search.doerId) result.doerId = search.doerId
  if (PRESETS.includes(search.preset as ActivityLogPreset)) result.preset = search.preset as ActivityLogPreset

  // `idle` was a boolean while the control had two states, so links shared
  // before this shipped carry `?idle=true` and the router parses it to a
  // boolean, not the string 'true'. Those links meant "include idle"; dropping
  // them would reopen a shared link on a different view than the sender saw,
  // silently. `false` maps to nothing because absent already means hidden.
  if (search.idle === true) result.idle = 'include'
  else if (search.idle === 'include' || search.idle === 'only') result.idle = search.idle

  // `source` is not read: the Source filter was removed with the Chrome
  // extension (ClickUp 14zf25zmr34), so a link that still carries
  // `?source=desktop|extension` opens on All rather than on a filter the
  // toolbar no longer shows.

  if (PRODUCTIVITY_STATUSES.includes(search.productivity as ActivityProductivity)) {
    result.productivity = search.productivity as ActivityProductivity
  }
  // Idle rows are never classified, so a status filter over idle-only can only
  // show nothing. The toolbar clears it on the same choice.
  if (result.idle === 'only') delete result.productivity

  if (DURATION_SORTS.includes(search.sort as DurationSort)) result.sort = search.sort as DurationSort
  const q = searchTermFromParam(search.q)
  if (q) result.q = q
  if (isSearchDate(search.from)) result.from = search.from
  if (isSearchDate(search.to)) result.to = search.to

  if (result.preset === 'shift') {
    if (isInstant(search.at)) result.at = search.at
    if (
      isInstant(search.start) &&
      isInstant(search.end) &&
      Date.parse(search.start) < Date.parse(search.end)
    ) {
      result.start = search.start
      result.end = search.end
    }
  }

  return result
}

/** Which event types the server should return for the idle filter.
 *
 *  The default view asks for both foreground types, so `foreground_domain`
 *  rows (written by the Chrome extension, still on some pilot laptops) keep
 *  showing with a Browser badge even though there is no longer a Source
 *  filter to pick them out (ClickUp 14zf25zmr34).
 *
 *  `agent_exit` (ClickUp 14zf25zj4hr) is the desktop agent reporting its own
 *  crash, so every view that shows foreground rows asks for it, and the crash
 *  row lands where the capture gap begins. Idle only is a narrower question
 *  and leaves it out: a crash row there would read as idle time.
 *
 *  Never returns an empty list. The events API reads no `event_type` as no
 *  filter and returns every type.
 *
 *  Spreads `DEFAULT_EVENT_TYPES` rather than returning it: it is a shared
 *  readonly array, and handing the same instance to every caller lets one of
 *  them mutate the defaults for all the others. */
export function eventTypesForFilters(idle: IdleFilter | undefined): AgentEventType[] {
  if (idle === 'only') return ['idle']
  const foreground: AgentEventType[] = [...DEFAULT_EVENT_TYPES]
  const withIdle: AgentEventType[] = idle === 'include' ? [...foreground, 'idle'] : foreground
  return [...withIdle, 'agent_exit']
}
